import { computed, type Ref, ref } from 'vue'

import { get } from '@/api'
import type {
  AppConfig,
  GalaxyEndpointConfig,
  WorkflowRepositoryConfig,
  WorkflowRepositoryOption,
} from '@/types/galaxy'

// State stored outside the function to persist across component instances
const config: Ref<AppConfig> = ref({})
const error: Ref<string | null> = ref(null)
const isLoading = ref(false)
const isConfigLoaded = computed(() => Object.keys(config.value).length > 0)

async function loadConfig(): Promise<void> {
  if (isConfigLoaded.value) return
  error.value = null
  isLoading.value = true
  try {
    const { status, data: configData } = await get('config/')
    if (status === 200) config.value = configData || {}
  } catch (err) {
    error.value = (err as Error)?.message || 'Failed to load configuration'
    console.error('Failed to load config:', err)
  } finally {
    isLoading.value = false
  }
}

async function reloadConfig(): Promise<void> {
  config.value = {}
  await loadConfig()
}

const galaxyEndpoints = computed<Record<string, GalaxyEndpointConfig>>(
  () => config.value.GALAXY_ENDPOINTS || {},
)

// The Galaxy instance the server itself talks to (settings.GALAXY_URL).
const galaxyUrl = computed(() => config.value.GALAXY_URL || '')

const publicWorkflowRepositories = computed<WorkflowRepositoryOption[]>(() => {
  const repos: Record<string, WorkflowRepositoryConfig> = {
    ...(config.value.DEFAULT_PUBLIC_WORKFLOWREPOSITORIES || {}),
  }

  // Add Galaxy endpoints with import_workflows enabled
  Object.entries(galaxyEndpoints.value).forEach(([url, endpoint]) => {
    if (endpoint.import_workflows && !repos[url]) {
      repos[url] = {
        label: endpoint.label,
        url: endpoint.url || url,
        api_url: endpoint.url || url,
        trs: null,
        isGalaxy: true,
      }
    }
  })

  return Object.entries(repos).map(([id, repo]) => ({
    id,
    value: id,
    label: repo.label || new URL(repo.url).hostname,
    url: repo.url,
    apiUrl: repo.api_url || repo.url,
    trs: repo.trs ?? null,
    urlObject: new URL(repo.url),
    isWorkflowHub: repo.url.includes('workflowhub'),
    isGalaxy: repo.isGalaxy || repo.url.includes('galaxy'),
  }))
})

export function useAppConfig() {
  return {
    loadConfig,
    reloadConfig,
    isLoading,
    isConfigLoaded,
    error,
    config,
    galaxyEndpoints,
    galaxyUrl,
    publicWorkflowRepositories,
  }
}
